import { useRef, useCallback, useState, useEffect } from 'react';
import Editor, { OnMount, OnChange } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import { Loader, Save, Check, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface MonacoCodeEditorProps {
  content: string;
  language: string;
  filename: string;
  readOnly?: boolean;
  projectId?: string;
  filePath?: string;
  /** Client ID，用于通过 client 接口保存文件（当 projectId 不可用时） */
  clientId?: string;
  onContentChange?: (content: string) => void;
  /** 保存成功后的回调，用于通知父组件更新状态 */
  onSave?: (content: string) => void;
}

// 语言映射：将文件扩展名映射到 Monaco 支持的语言
const getMonacoLanguage = (filename: string): string => {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  const langMap: Record<string, string> = {
    // JavaScript/TypeScript
    js: 'javascript',
    jsx: 'javascript',
    mjs: 'javascript',
    cjs: 'javascript',
    ts: 'typescript',
    tsx: 'typescript',
    // Web
    html: 'html',
    htm: 'html',
    css: 'css',
    scss: 'scss',
    sass: 'scss',
    less: 'less',
    vue: 'html',
    svelte: 'html',
    // Data
    json: 'json',
    jsonc: 'json',
    json5: 'json',
    xml: 'xml',
    yaml: 'yaml',
    yml: 'yaml',
    toml: 'ini',
    ini: 'ini',
    // Programming
    py: 'python',
    pyw: 'python',
    java: 'java',
    c: 'c',
    cpp: 'cpp',
    cc: 'cpp',
    cxx: 'cpp',
    h: 'c',
    hpp: 'cpp',
    cs: 'csharp',
    go: 'go',
    rs: 'rust',
    rb: 'ruby',
    php: 'php',
    swift: 'swift',
    kt: 'kotlin',
    scala: 'scala',
    lua: 'lua',
    pl: 'perl',
    r: 'r',
    R: 'r',
    sql: 'sql',
    // Shell
    sh: 'shell',
    bash: 'shell',
    zsh: 'shell',
    ps1: 'powershell',
    bat: 'bat',
    cmd: 'bat',
    // Markup
    md: 'markdown',
    markdown: 'markdown',
    tex: 'latex',
    // Other
    dockerfile: 'dockerfile',
    graphql: 'graphql',
    gql: 'graphql',
    proto: 'protobuf',
    txt: 'plaintext',
  };
  return langMap[ext] || 'plaintext';
};

// 保存文件内容到后端
const saveFileContent = async (
  projectId: string,
  filePath: string,
  content: string
): Promise<void> => {
  const token = localStorage.getItem('user-token');
  const response = await fetch(`/api/projects/${projectId}/files/content`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ filePath, content }),
  });

  if (!response.ok) {
    throw new Error(`Failed to save file: ${response.statusText}`);
  }
};

// 🔥 新增：通过 client 接口保存文件内容（当 projectId 不可用时使用）
const saveFileContentViaClient = async (
  clientId: string,
  filePath: string,
  content: string
): Promise<void> => {
  const token = localStorage.getItem('user-token');
  const response = await fetch(`/api/clients/${clientId}/files/content`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ file: filePath, content }),
  });

  if (!response.ok) {
    throw new Error(`Failed to save file: ${response.statusText}`);
  }
};

export default function MonacoCodeEditor({
  content,
  language,
  filename,
  readOnly = false,
  projectId,
  filePath,
  clientId,
  onContentChange,
  onSave,
}: MonacoCodeEditorProps) {
  const { t } = useTranslation();
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const [saveStatus, setSaveStatus] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle');
  const [currentContent, setCurrentContent] = useState(content);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedContentRef = useRef(content);

  // 获取 Monaco 支持的语言
  const monacoLanguage = language || getMonacoLanguage(filename);

  // 是否可以保存（需要 projectId 或 clientId，以及 filePath）
  const canSave = !readOnly && filePath && (projectId || clientId);

  // 编辑器挂载回调
  const handleEditorDidMount: OnMount = useCallback(editor => {
    editorRef.current = editor;
    // 配置编辑器
    editor.updateOptions({
      minimap: { enabled: true },
      scrollBeyondLastLine: false,
      fontSize: 14,
      lineNumbers: 'on',
      renderWhitespace: 'selection',
      tabSize: 2,
      wordWrap: 'on',
      automaticLayout: true,
    });
  }, []);

  // 内容变化回调（带防抖自动保存）
  const handleEditorChange: OnChange = useCallback(
    value => {
      if (value === undefined) return;

      setCurrentContent(value);
      setHasUnsavedChanges(value !== lastSavedContentRef.current);
      onContentChange?.(value);

      // 如果可以保存，启动防抖自动保存
      if (canSave) {
        // 清除之前的定时器
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }

        // 设置新的定时器（1秒后保存）
        saveTimeoutRef.current = setTimeout(async () => {
          if (value === lastSavedContentRef.current) return;

          setSaveStatus('saving');
          try {
            // 🔥 根据 projectId 或 clientId 选择合适的保存 API
            if (projectId) {
              await saveFileContent(projectId, filePath!, value);
            } else if (clientId) {
              await saveFileContentViaClient(clientId, filePath!, value);
            }
            lastSavedContentRef.current = value;
            setHasUnsavedChanges(false);
            setSaveStatus('saved');
            // 🔥 保存成功后通知父组件更新状态
            onSave?.(value);
            // 2秒后恢复 idle 状态
            setTimeout(() => setSaveStatus('idle'), 2000);
          } catch (error) {
            console.error('Failed to save file:', error);
            setSaveStatus('error');
            // 3秒后恢复 idle 状态
            setTimeout(() => setSaveStatus('idle'), 3000);
          }
        }, 1000);
      }
    },
    [canSave, projectId, filePath, clientId, onContentChange, onSave]
  );

  // 清理定时器
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // 当 content prop 变化时更新编辑器内容
  useEffect(() => {
    setCurrentContent(content);
    lastSavedContentRef.current = content;
    setHasUnsavedChanges(false);
  }, [content]);

  // 手动保存
  const handleManualSave = useCallback(async () => {
    if (!canSave || !hasUnsavedChanges) return;

    setSaveStatus('saving');
    try {
      // 🔥 根据 projectId 或 clientId 选择合适的保存 API
      if (projectId) {
        await saveFileContent(projectId, filePath!, currentContent);
      } else if (clientId) {
        await saveFileContentViaClient(clientId, filePath!, currentContent);
      }
      lastSavedContentRef.current = currentContent;
      setHasUnsavedChanges(false);
      setSaveStatus('saved');
      // 🔥 保存成功后通知父组件更新状态
      onSave?.(currentContent);
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to save file:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [
    canSave,
    hasUnsavedChanges,
    projectId,
    filePath,
    clientId,
    currentContent,
    onSave,
  ]);

  // 键盘快捷键保存 (Ctrl+S / Cmd+S)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleManualSave();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleManualSave]);

  return (
    <div
      className={`h-full flex flex-col bg-white ${readOnly ? 'monaco-readonly' : ''}`}
    >
      {/* 🔥 隐藏只读提示消息的 CSS */}
      {readOnly && (
        <style>{`
          .monaco-readonly .monaco-editor-overlaymessage {
            display: none !important;
          }
        `}</style>
      )}
      {/* 状态栏 */}
      {canSave && (
        <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50 border-b border-gray-200 text-xs">
          <div className="flex items-center gap-2 text-gray-600">
            <span className="font-mono">{monacoLanguage}</span>
            {hasUnsavedChanges && (
              <span className="text-amber-600">
                {t('monacoCodeEditor.unsavedChanges')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {saveStatus === 'saving' && (
              <span className="flex items-center gap-1 text-blue-600">
                <Loader size={12} className="animate-spin" />
                {t('monacoCodeEditor.saving')}
              </span>
            )}
            {saveStatus === 'saved' && (
              <span className="flex items-center gap-1 text-green-600">
                <Check size={12} />
                {t('monacoCodeEditor.saved')}
              </span>
            )}
            {saveStatus === 'error' && (
              <span className="flex items-center gap-1 text-red-600">
                <AlertCircle size={12} />
                {t('monacoCodeEditor.saveFailed')}
              </span>
            )}
            <button
              onClick={handleManualSave}
              disabled={!hasUnsavedChanges || saveStatus === 'saving'}
              className={cn(
                'flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors',
                hasUnsavedChanges && saveStatus !== 'saving'
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              )}
              title={t('monacoCodeEditor.saveTitle')}
            >
              <Save size={12} />
              {t('monacoCodeEditor.save')}
            </button>
          </div>
        </div>
      )}

      {/* Monaco Editor */}
      <div className="flex-1 min-h-0 py-2">
        <Editor
          height="100%"
          language={monacoLanguage}
          value={currentContent}
          theme="vs" // 浅色主题
          onMount={handleEditorDidMount}
          onChange={handleEditorChange}
          options={{
            readOnly,
            domReadOnly: readOnly, // 🔥 DOM 级别禁用编辑
            readOnlyMessage: { value: '' }, // 🔥 设置空消息，完全隐藏提示
            minimap: { enabled: true },
            scrollBeyondLastLine: false,
            fontSize: 14,
            lineNumbers: 'on',
            renderWhitespace: 'selection',
            tabSize: 2,
            wordWrap: 'on',
            automaticLayout: true,
            padding: { top: 16, bottom: 16 }, // 🔥 添加上下内边距
            scrollbar: {
              vertical: 'visible',
              horizontal: 'visible',
            },
          }}
          loading={
            <div className="flex items-center justify-center h-full">
              <Loader className="animate-spin text-gray-400" size={24} />
            </div>
          }
        />
      </div>
    </div>
  );
}
